import { useEffect, MouseEvent } from 'react';
import Link from 'next/link';
import useTranslation from 'next-translate/useTranslation';
import Button from '#components/controls/button/Button';
import FavouriteButton from '#components/controls/buttons/favouriteButton/FavouriteButton';
import ButtonShare from '#components/controls/buttons/buttonShare/ButtonShare';
import { useOvermindActions, useOvermindState } from '#store/overmind';
import { isEuropaParkApp } from '#utils/isEuropaParkApp';
import ResortPassIcon from '#components/icons/ResortPass';
import {
  ContentType,
  Media,
  Product,
  ProductAccess,
  ProductAccessReason,
  ProductType,
} from '#types/generated/graphql-types';
import { useGetButtonParadeDataLazyQuery } from '#types/generated/apollo-client';
import {
  Content360,
  DrmProtectedContent,
  PlayArrow,
  PlayCircleOutlined,
} from '#components/icons';
import { getRoute } from '#utils';
import { useLoginNotificationDialog } from '#modules/dialog-login-notification/DialogLoginNotification';
import { useRentNotificationDialog } from '#modules/dialog-rent-notification/DialogRentNotification';
import RentingTimeNotification from '#components/hero-button-parade/RentingTimeNotification';
import CalendarButton from '#components/hero-button-parade/CalendarButton';
import getConfigVars from '#utils/get-config-vars';
import shouldShowPlayButton from '#utils/shouldShowPlayButton';
import shouldShowRentButton from '#utils/shouldShowRentButton';
import TooltipInfoText from '#components/hero-button-parade/TooltipInfoText';
import { VIDEO_FORMAT_360 } from '#config/constants';
import dynamic from 'next/dynamic';
import useContinueWatching from '#hooks/useContinueWatching';
import { triggerPlayerPlay } from '#utils/player/controls';
import isNonArchivedLiveStream from '#utils/livestream/isNonArchivedLivestream';
import mapDetailsButtonLabel from '#utils/livestream/mapDetailsButtonLabel';
import getCalendarFileUrl from '#utils/livestream/getCalendarFileUrl';
import useResortPass from '#hooks/useResortPass';

const DrmProtectedDisclaimer = dynamic(
  () => import('#components/disclaimers/DrmProtectedDisclaimer')
);
const ResortPassDisclaimer = dynamic(
  () => import('#components/disclaimers/ResortPassDisclaimer')
);
const Video360Disclaimer = dynamic(
  () => import('#components/disclaimers/Content360Disclaimer')
);

const { BASE_URL } = getConfigVars({ BASE_URL: '' });

type Props = {
  media?: Media;
  mediaIri: string;
  showPurchaseButton?: boolean;
  canShowDetailsButton?: boolean;
  showTrailers?: boolean;
};

function canPlayDirectly(media): boolean {
  if (isNonArchivedLiveStream(media)) return false;

  return shouldShowPlayButton(media?.products);
}

function canBePurchased(rentProduct: Product, access: ProductAccess): boolean {
  return rentProduct?.price > 0 && shouldShowRentButton(rentProduct, access);
}

const HeroButtonParade = ({
  media,
  mediaIri,
  canShowDetailsButton = false,
  showPurchaseButton = true,
  showTrailers = true,
}: Props) => {
  const { t, lang: locale } = useTranslation();
  const { initPlayer } = useOvermindActions().player;
  const { profile } = useOvermindState().user;

  const { hasResortPass, isResortPassProduct } = useResortPass();

  const { showLoginNotificationDialog } = useLoginNotificationDialog();
  const { showRentNotificationDialog } = useRentNotificationDialog();

  const [loadButtonParadeData, { data }] = useGetButtonParadeDataLazyQuery({
    variables: {
      mediaIri,
    },
  });
  const _media = data?.media || media;
  const _products = _media?.products;
  const _trailers = _media?.trailers;
  const rentProduct: Product = _products?.availableProducts?.find(
    (product) => product?.type === ProductType.Rent
  );
  const subscriptionProduct: Product = _products?.availableProducts?.find(
    (product) => product?.type === ProductType.Subscription
  );
  const resortPassPermission = _products?.availableProducts?.some(
    (product) =>
      product?.type === ProductType.MackstoreWallet &&
      isResortPassProduct(product)
  ) ?? false;
  const { deniedRegions, allowedRegions } = _media?.geoRestrictions || {};
  const isDrmProtected =
    deniedRegions?.length > 0 || allowedRegions?.length > 0;
  const shareUri = `${BASE_URL}${getRoute(_media, t, locale)}`;
  const downloadUri = getCalendarFileUrl(BASE_URL, locale, media);
  const detailsButtonLabel = mapDetailsButtonLabel(_media as Media);
  const canPlay = canPlayDirectly(_media);
  const is360Video = (_media.tags ?? []).find(
    (tag) => tag === VIDEO_FORMAT_360
  );

  /**
   * Teaser button should be displayed only inside the HeroSlider when:
   * - the media has no products OR
   * - for all livestreams except archived ones
   */
  const showDetailsButton =
    canShowDetailsButton &&
    canPlay === false &&
    canBePurchased(rentProduct, media?.products?.access) === false;

  useEffect(() => {
    if (isNonArchivedLiveStream(media)) return;

    if (profile) {
      loadButtonParadeData();
    }
  }, [profile]);

  const handlePlayButtonClick = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();

    //when the user is not logged
    if (
      !profile &&
      _products?.access?.reason === ProductAccessReason.LoginRequired
    ) {
      showLoginNotificationDialog();
      return;
    }

    initPlayer({ iri: mediaIri, contentType: _media?.contentType });

    triggerPlayerPlay();
  };

  const handleRentButtonClick = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    if (!profile) {
      showLoginNotificationDialog('purchase');
      return;
    }

    showRentNotificationDialog(mediaIri, rentProduct.iri);
  };

  const handleTrailerButtonClick = (item) => {
    initPlayer({ iri: item?.iri });
    triggerPlayerPlay();
  };

  const { continueWatchingItem } = useContinueWatching(_media?.iri);

  const buttonText = t(
    _media.contentType === ContentType.Audio
      ? 'teaserButtonPlayAudio'
      : 'teaserButtonPlay'
  );

  const buttonLayout =
    '[&:not(:last-of-type)]:small:tw-w-full [&:last-of-type]:small:tw-grow';

  return (
    <div className="tw-flex tw-flex-col">
      {!hasResortPass && (subscriptionProduct || resortPassPermission) && (
        <RentingTimeNotification
          date={data?.media?.products?.access?.endDate}
        />
      )}
      <div className="tw-flex tw-gap-4 small:tw-flex-wrap tw-items-center tw-transition-opacity tw-delay-500 tw-duration-500 tw-opacity-100">
        {canPlay && (
          <>
            {!!continueWatchingItem && (
              <Button
                classes={buttonLayout}
                data-testid="media-action-button"
                startIcon={
                  <PlayCircleOutlined
                    duration={continueWatchingItem.mediaDuration}
                    time={continueWatchingItem.currentTime}
                  />
                }
                title={buttonText}
                onClick={handlePlayButtonClick}
              />
            )}

            {!continueWatchingItem && (
              <Button
                classes={buttonLayout}
                data-testid="media-action-button"
                startIcon={<PlayArrow />}
                title={buttonText}
                onClick={handlePlayButtonClick}
              />
            )}
          </>
        )}

        {showPurchaseButton &&
          canBePurchased(rentProduct, _media?.products?.access) && (
            <Button
              classes={buttonLayout}
              data-testid="media-action-button"
              title={t('productButtonRent', {
                price: `${Number(rentProduct?.price / 100).toLocaleString(
                  locale,
                  {
                    style: 'currency',
                    currency: 'EUR',
                  }
                )}${'\u00B9'}`,
              })}
              onClick={handleRentButtonClick}
            />
          )}
        {showDetailsButton && (
          <Link
            href={getRoute(_media, t, locale)}
            passHref={true}
            legacyBehavior={true}
          >
            <Button classes={buttonLayout} title={t(detailsButtonLabel)} />
          </Link>
        )}

        {showTrailers &&
          (_trailers ?? []).map((item) => (
            <Button
              classes={buttonLayout}
              data-testid="media-action-button"
              key={item.id}
              startIcon={<PlayArrow />}
              variant="outlined"
              title={t('trailer')}
              onClick={(event) => {
                event.preventDefault();
                handleTrailerButtonClick(item);
              }}
            />
          ))}

        <div className="tw-flex tw-gap-4">
          {!!profile && _media && <FavouriteButton item={_media} />}
          {!!downloadUri && <CalendarButton downloadUrl={downloadUri} />}
          {!isEuropaParkApp() && (
            <ButtonShare
              title={_media?.title}
              description={_media?.teaserDescription}
              uri={shareUri}
            />
          )}
        </div>
      </div>

      {(subscriptionProduct || resortPassPermission) && (
        <TooltipInfoText
          tooltipIcon={<ResortPassIcon size={24} />}
          textTranslationKey={
            hasResortPass ? 'resortPassOwnersInfoText' : 'resortPassInfoText'
          }
          disclaimer={
            <ResortPassDisclaimer
              hasResortPass={hasResortPass}
              isLoggedIn={!!profile}
            />
          }
        />
      )}

      {isDrmProtected && (
        <TooltipInfoText
          tooltipIcon={<DrmProtectedContent size={24} />}
          textTranslationKey={'geoblockingInfoText'}
          disclaimer={
            <DrmProtectedDisclaimer
              allowedRegions={allowedRegions}
              deniedRegions={deniedRegions}
            />
          }
        />
      )}

      {is360Video && (
        <TooltipInfoText
          tooltipIcon={<Content360 size={24} />}
          textTranslationKey={'360ContentInfoText'}
          disclaimer={<Video360Disclaimer />}
        />
      )}
    </div>
  );
};

export default HeroButtonParade;
